// The service that call the API to get the data from VEHICLE_API_URL

import { handleAxiosError } from '../../common/application/handleAxiosError';
import useParkstoryApi, { type ApiResponse, Target } from '../../common/data/parkstory/parkstory';
import useFleetConvertor, { type Fleet, type ApiFleetAttributes } from '../domain/fleet';
import { type Vehicle, type VehicleType, type VehicleLookupResult, nameOf } from '../domain/vehicle';

import { type ApiEntities, type ApiEntity, mapApiEntities, mapApiEntity } from '@/common/domain/api';
import { logException } from '@/common/utils/logger';

const useFleetApi = () => {

    const { get, post, put, remove, convertApiResponse } = useParkstoryApi();
    const { apiFleetToFleet, apiFleetsToFleets } = useFleetConvertor;
    

    const apiGetFleets = async (): Promise<ApiResponse<Fleet[]>> => {
        try {
            const response = await get<ApiResponse<ApiEntity<ApiFleetAttributes>[]>>(Target.Vehicle, 'fleets?populate[vehicles][populate][vehicleType]=true');
            return convertApiResponse(response, apiFleetsToFleets);
        } catch (error: any) {
            throw (handleAxiosError(error, 'getFleets'));
        }
    };

    // Fetch specific fleets by identifier. Used to resolve a group's assigned fleets when they are
    // missing from the global fleet list (e.g. a plain member whose `GET /fleets` is scoped).
    // A fleet relation's `targetId` is now the string `documentId`, but groups saved before the
    // documentId migration still carry a numeric id — so partition the two and filter each on its
    // own field (documentId strings vs numeric ids) via `$or`.
    const apiGetFleetsByIds = async (ids: Array<string | number>): Promise<ApiResponse<Fleet[]>> => {
        try {
            const documentIds = ids.map(String).filter(id => !/^\d+$/.test(id));
            const numericIds = ids.map(String).filter(id => /^\d+$/.test(id));

            const orClauses: string[] = [];
            let orIndex = 0;
            if (documentIds.length) {
                documentIds.forEach((id, i) => orClauses.push(`filters[$or][${orIndex}][documentId][$in][${i}]=${encodeURIComponent(id)}`));
                orIndex++;
            }
            if (numericIds.length) {
                numericIds.forEach((id, i) => orClauses.push(`filters[$or][${orIndex}][id][$in][${i}]=${encodeURIComponent(id)}`));
                orIndex++;
            }
            /*
             * With no clauses the query would carry no filter at all and return EVERY readable
             * fleet — and callers feed this straight into a delete loop, so that would wipe fleets
             * the group never owned (a member's personal fleet included). Never widen to "all".
             */
            if (orClauses.length === 0) {
                return { data: [], meta: undefined, status: { code: 200, text: 'ok' } } as unknown as ApiResponse<Fleet[]>;
            }
            const filters = orClauses.join('&');
            const response = await get<ApiResponse<ApiEntity<ApiFleetAttributes>[]>>(
                Target.Vehicle,
                `fleets?${filters}&populate[vehicles][populate][vehicleType]=true`
            );
            return convertApiResponse(response, apiFleetsToFleets);
        } catch (error: any) {
            throw (handleAxiosError(error, 'getFleetsByIds'));
        }
    };

    const apiGetFleet = async (documentId: string): Promise<ApiResponse<Fleet>> => {
        try {
            const response = await get<ApiEntity<ApiFleetAttributes>>(
                Target.Vehicle,
                `fleets/${documentId}?populate[vehicles][populate][vehicleType]=true`
            );
            return convertApiResponse(response, apiFleetToFleet);
        } catch (error: any) {
            throw (handleAxiosError(error, 'getFleet'));
        }
    };

    // Pass `groupRef` (the group's IDM ref) to create the fleet AS that group's fleet — the backend
    // links it (relatesTo) and grants the group read access. Omit it for a standalone fleet.
    const apiCreateFleet = async (
        fleet: Fleet & { groupRef?: string }
    ): Promise<ApiResponse<string>> => {
        try {
            const response = await post<ApiEntity<unknown>>(Target.Vehicle, 'fleets', fleet);
            const createdDocumentId = response.data.documentId;
            if (!createdDocumentId) {
                throw new Error('Fleet creation response is missing documentId');
            }
            return {
                ...response,
                data: createdDocumentId
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'createFleet'));
        }
    };

    // Pass `groupRef` to unassign the fleet from that group (remove relation + revoke access) as
    // part of the delete. Omit it to delete a standalone fleet.
    const apiDeleteFleet = async (documentId: string, groupRef?: string): Promise<ApiResponse<string>> => {
        try {
            const response = await remove<ApiEntity<unknown>>(Target.Vehicle, `fleets/${documentId}`, groupRef !== undefined ? { groupRef } : undefined);
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error: any) {
            logException(error, { documentId });
            throw (handleAxiosError(error, 'deleteFleet'));
        }
    };

    const apiUpdateFleet = async (fleet: Fleet): Promise<ApiResponse<string>> => {
        try {
            if (!fleet.documentId) {
                throw new Error('Fleet documentId is required to update a fleet');
            }
            const response = await put<ApiEntity<unknown>>(Target.Vehicle, `fleets/${fleet.documentId}`, fleet);
            const updatedDocumentId = response.data.documentId;
            if (!updatedDocumentId) {
                throw new Error('Fleet update response is missing documentId');
            }
            return {
                ...response,
                data: updatedDocumentId
            };
        } catch (error: any) {
            logException(error, { fleetId: fleet.documentId });
            throw (handleAxiosError(error, 'updateFleet'));
        }
    };

    const apiGetVehicle = async (documentId: string): Promise<ApiResponse<Vehicle | null>> => {
        try {
            const response = await get<ApiEntity<Vehicle>>(Target.Vehicle, `vehicles/${documentId}?populate[vehicleType]=true`);
            return convertApiResponse<Vehicle, ApiEntity<Vehicle>>(response, mapApiEntity<Vehicle>);
        } catch (error: any) {
            throw (handleAxiosError(error, 'getVehicle'));
        }
    };

    const apiGetVehicles = async (): Promise<ApiResponse<Vehicle[]>> => {
        try {
            const response = await get<ApiEntities<Vehicle>>(Target.Vehicle, 'vehicles/');
            const convertedResponse = convertApiResponse(response, mapApiEntities);

            return {
                data: (convertedResponse.data).filter((vehicle: Vehicle) => vehicle?.documentId !== undefined),
                meta: response.meta,
                status: response.status,
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'getVehicles'));
        }
    };

    // Assistance-only lookup (nothing created/stored): pre-fills the vehicle creation form from the
    // national registry matching `countryCode`. Currently only `nl` is supported server-side — any
    // other value 400s, which the caller treats as "no match, fill in manually".
    const apiLookupVehicleByPlate = async (plate: string, countryCode: string): Promise<ApiResponse<VehicleLookupResult>> => {
        try {
            return await get<VehicleLookupResult>(
                Target.Vehicle,
                `vehicles/external-api-lookup?plate=${encodeURIComponent(plate)}&countryCode=${encodeURIComponent(countryCode)}`
            );
        } catch (error: any) {
            throw (handleAxiosError(error, 'lookupVehicle'));
        }
    };

    const apiGetVehicleTypes = async (): Promise<ApiResponse<VehicleType[]>> => {
        try {
            const response = await get<ApiEntities<VehicleType>>(Target.Vehicle, 'vehicle-types/findAll');
            const convertedData = convertApiResponse<VehicleType[], ApiEntities<VehicleType>>(response, mapApiEntities<VehicleType>);

            return convertedData;
        } catch (error: any) {
            throw (handleAxiosError(error, 'getVehicles'));
        }
    };

    const apiGetVehiclesFromFleet = async (
        documentId: string
    ): Promise<ApiResponse<Vehicle[]>> => {
        try {
            const response = await get<ApiEntities<Vehicle>>(Target.Vehicle, `fleets/${documentId}?populate[vehicles][populate][vehicleType]=true`);
            return convertApiResponse(response, mapApiEntities<Vehicle>);
        } catch (error: any) {
            throw (handleAxiosError(error, 'getVehiclesFromFleet'));
        }
    };

    const apiGetVehicleFromFleet = async (
        fleetDocumentId: string,
        vehicleDocumentId: string
    ): Promise<ApiResponse<Vehicle | null>> => {
        try {
            const response = await get<ApiEntity<Vehicle>>(
                Target.Vehicle,
                `fleets/${fleetDocumentId}/vehicles/${vehicleDocumentId}?populate[vehicleType]=true`
            );
            return convertApiResponse<Vehicle, ApiEntity<Vehicle>>(response, mapApiEntity<Vehicle>);
        } catch (error: any) {
            throw (handleAxiosError(error, 'getVehicleFromFleet'));
        }
    };

    /*
     * `vehicleType` is a relation: sending the populated object back leaves it unset, which is why
     * saved vehicles came back from a fleet read with no vehicleType at all. Send its id, plus the
     * ref string in `type` for the API's own denormalised column.
     *
     * A `vehicle` loaded from the API (via mapApiEntity) also carries `id`/`documentId` and Strapi's
     * own read-only metadata (`createdAt`/`updatedAt`/`publishedAt`) — Strapi's validator rejects
     * those on write, so strip them here once rather than in every caller.
     */
    const vehicleToApiBody = (vehicle: Vehicle) => {
        const { id, documentId, createdAt, updatedAt, publishedAt, ...body } = vehicle as Vehicle & {
            id?: unknown; createdAt?: unknown; updatedAt?: unknown; publishedAt?: unknown;
        };
        return {
            ...body,
            vehicleType: vehicle.vehicleType?.documentId ?? vehicle.vehicleType?.id,
            type: nameOf(vehicle.vehicleType),
        };
    };

    const apiCreateVehicle = async (
        vehicle: Vehicle,
        fleetDocumentId?: string
    ): Promise<ApiResponse<string>> => {
        try {
            if (fleetDocumentId === null || fleetDocumentId === undefined) {
                throw new Error('Fleet documentId is required to create a vehicle');
            }
            const response = await post<ApiEntity<unknown>>(
                Target.Vehicle,
                `fleets/${fleetDocumentId}/vehicle`,
                vehicleToApiBody(vehicle),
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'createVehicle'));
        }
    };

    const apiUpdateVehicle = async (
        vehicle: Vehicle
    ): Promise<ApiResponse<string>> => {
        try {
            const response = await put<ApiEntity<unknown>>(
                Target.Vehicle,
                `vehicles/${vehicle.documentId}`,
                vehicleToApiBody(vehicle)
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'updateVehicle'));
        }
    };

    const apiDeleteVehicle = async (documentId: string): Promise<ApiResponse<string>> => {
        try {
            const response = await remove<ApiEntity<unknown>>(Target.Vehicle, `vehicles/${documentId}`);
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'deleteVehicle'));
        }
    };

    const apiAddVehicleToFleet = async (
        fleetDocumentId: string,
        vehicleIdArr: string[]
    ): Promise<ApiResponse<string>> => {
        try {
            const response = await post<ApiEntity<unknown>>(
                Target.Vehicle,
                `fleets/${fleetDocumentId}/vehicles`,
                vehicleIdArr
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'addVehicle'));
        }
    };

    const apiRemoveVehiclesFromFleet = async (
        fleetDocumentId: string,
        vehicles: string[]
    ): Promise<ApiResponse<string>> => {
        try {
            const response = await put<ApiEntity<unknown>>(
                Target.Vehicle,
                `fleets/${fleetDocumentId}/revoke/vehicle`,
                { vehicles },
            );
            return {
                ...response,
                data: response.data.documentId ?? ''
            };
        } catch (error: any) {
            throw (handleAxiosError(error, 'removeVehicles'));
        }
    };

    return {
        apiAddVehicleToFleet,
        apiCreateFleet,
        apiCreateVehicle,
        apiDeleteFleet,
        apiDeleteVehicle,
        apiGetFleet,
        apiGetFleets,
        apiGetFleetsByIds,
        apiGetVehicle,
        apiGetVehicleFromFleet,
        apiGetVehicles,
        apiGetVehiclesFromFleet,
        apiLookupVehicleByPlate,
        apiRemoveVehiclesFromFleet,
        apiUpdateFleet,
        apiUpdateVehicle,
        apiGetVehicleTypes
    };
};

export default useFleetApi;
